import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Tools|Accredible|Candidate Emails|';
const sharedProperties: Pick<LocDef, 'shared' | 'feature' | 'portal'> = {
	shared: false,
	feature: HubFeature.ShareAccredibleBadging,
	portal: 'SP',
};

type LocNameStart = 'SPToolsAccredibleManagement';
export type SPToolsAccredibleManagementLocNames =
	| `${LocNameStart}TitleSection`
	| `${LocNameStart}TitleTable`
	| `${LocNameStart}ContentDeleteEmailInstructions`
	| `${LocNameStart}ButtonSetPreferred`
	| `${LocNameStart}ButtonDeleteId`
	| `${LocNameStart}ContentDeletePreferredInfo`
	| `${LocNameStart}ContentDeleteInBadges`
	| `${LocNameStart}NotificationDeleteSuccess`
	| `${LocNameStart}NotificationDeleteFailure`
	| `${LocNameStart}NotificationSetPreferredSuccess`
	| `${LocNameStart}LabelCmcId`
	| `${LocNameStart}LabelFirstName`
	| `${LocNameStart}LabelLastName`
	| `${LocNameStart}LabelDuplicateEmail`
	| `${LocNameStart}LabelPreferred`;

export const spToolsAccredibleManagementLocs: LocDefs<SPToolsAccredibleManagementLocNames> = {
	SPToolsAccredibleManagementTitleSection: {
		defaultValue: 'Candidate Emails',
		description: 'Section Title',
		displayPath: `${pathStart}Section Title`,
		displayType: 'Title',
		legacyName: 'Share.AccredibleBadging/Accredible.BadgeSearch.Title.ManageAccredible',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementTitleTable: {
		defaultValue: 'Candidate Emails',
		description: 'Table Title',
		displayPath: `${pathStart}Table Title`,
		displayType: 'Title',
		legacyName: 'Share.AccredibleBadging/Accredible.BadgeSearch.Title.CandidateEmailsSection',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementContentDeleteEmailInstructions: {
		defaultValue: '',
		description: 'Delete Email Instructions',
		displayPath: `${pathStart}Delete Email Instructions`,
		displayType: 'Content',
		legacyName:
			'Share.AccredibleBadging/Accredible.BadgeSearch.Content.DeleteEmailInstructions',
		htmlAllowed: true,
		...sharedProperties,
	},
	SPToolsAccredibleManagementButtonSetPreferred: {
		defaultValue: 'Set Preferred',
		description: 'Set Preferred button',
		displayPath: `${pathStart}Set Preferred`,
		displayType: 'Button',
		legacyName: 'Share.AccredibleBadging/Accredible.BadgeSearch.Button.SetPreferred',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementButtonDeleteId: {
		defaultValue: 'Delete External Id',
		description: 'Delete External Id button',
		displayPath: `${pathStart}Delete External Id`,
		displayType: 'Button',
		legacyName: 'Share.AccredibleBadging/Accredible.BadgeSearch.Button.DeleteId',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementContentDeletePreferredInfo: {
		defaultValue:
			'This is the preferred email of the candidate with Accredible. It cannot be deleted, unless a different email is set as a preferred email.',
		description: 'Delete preferred information',
		displayPath: `${pathStart}Delete preferred information`,
		displayType: 'Content',
		legacyName: 'Share.AccredibleBadging/Accredible.BadgeSearch.Content.DeletePreferredInfo',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementContentDeleteInBadges: {
		defaultValue:
			'You may only delete emails that do not have any pending, accepted or rejected badges at Accredible. If you want to remove an old email, you would need to contact the client or Accredible and have them delete the badges first.',
		description: 'Delete in badges information',
		displayPath: `${pathStart}Delete in badges information`,
		displayType: 'Content',
		legacyName: 'Share.AccredibleBadging/Accredible.BadgeSearch.Content.DeleteInBadges',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementNotificationDeleteSuccess: {
		defaultValue: 'Candidate External Id was succesfully deleted',
		description: 'Delete success notification',
		displayPath: `${pathStart}Delete success`,
		displayType: 'Notification',
		legacyName:
			'Share.AccredibleBadging/Accredible.BadgeSearch.NotificationMessage.DeleteSuccess',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementNotificationDeleteFailure: {
		defaultValue: 'Something went wrong, please contact support',
		description: 'Delete failure notification',
		displayPath: `${pathStart}Delete failure`,
		displayType: 'Notification',
		legacyName:
			'Share.AccredibleBadging/Accredible.BadgeSearch.NotificationMessage.DeleteFailure',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementNotificationSetPreferredSuccess: {
		defaultValue: 'Set preferred successful',
		description: 'Set preferred successful',
		displayPath: `${pathStart}Set preferred successful`,
		displayType: 'Notification',
		legacyName:
			'Share.AccredibleBadging/Accredible.BadgeSearch.Notification.SetPreferredSuccessful',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementLabelCmcId: {
		defaultValue: 'CMC ID',
		description: 'Label for: CMC ID.',
		displayPath: `${pathStart}CMC ID`,
		displayType: 'Label',
		legacyName: 'Share.AccredibleBadging/Accredible.BadgeSearch.ColumnHeading.CMCId',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementLabelFirstName: {
		defaultValue: 'First Name',
		description: 'Label for: First Name.',
		displayPath: `${pathStart}First Name`,
		displayType: 'Label',
		legacyName: 'Share.AccredibleBadging/Accredible.BadgeSearch.ColumnHeading.FirstName',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementLabelLastName: {
		defaultValue: 'Last Name',
		description: 'Label for: Last Name.',
		displayPath: `${pathStart}Last Name`,
		displayType: 'Label',
		legacyName: 'Share.AccredibleBadging/Accredible.BadgeSearch.ColumnHeading.LastName',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementLabelDuplicateEmail: {
		defaultValue: 'Email',
		description: 'Label for: Email.',
		displayPath: `${pathStart}Email`,
		displayType: 'Label',
		legacyName: 'Share.AccredibleBadging/Accredible.BadgeSearch.ColumnHeading.DuplicateEmail',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPToolsAccredibleManagementLabelPreferred: {
		defaultValue: 'Preferred',
		description: 'Label for: Preferred.',
		displayPath: `${pathStart}Preferred`,
		displayType: 'Label',
		legacyName: 'Share.AccredibleBadging/Accredible.BadgeSearch.ColumnHeading.Preferred',
		htmlAllowed: false,
		...sharedProperties,
	},
};

export type SPToolsAccredibleManagementLocType = LocMap<SPToolsAccredibleManagementLocNames>;
